import { Button, Input, List, Space, Tag, Typography, message, theme } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { useState } from "react";
import {
  addRoadmapStage,
  deleteRoadmapStage,
  reorderRoadmapStages,
} from "../../repository/data.repository";
import { RoadmapStage } from "../../repository/data.default";
import { useOrderSwap } from "../../hooks/useOrderSwap";
import ConfirmDeleteButton from "../../controls/ConfirmDeleteButton";
import DraftTag from "../../controls/DraftTag";

export interface RoadmapStagesRailProps {
  readonly roadmapUid: string;
  readonly stages: RoadmapStage[];
  readonly selectedUid?: string | null;
  readonly onSelect: (uid: string) => void;
  readonly onChanged: () => void | Promise<void>;
}

function RoadmapStagesRail(props: RoadmapStagesRailProps) {
  const { token } = theme.useToken();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { move, busy: reordering } = useOrderSwap(props.stages, reorderRoadmapStages, props.onChanged);

  function onAdd(): void {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    addRoadmapStage({
      roadmapUid: props.roadmapUid,
      name: trimmed,
      order: props.stages.length + 1,
    })
      .then((created: RoadmapStage) => {
        setName("");
        return Promise.resolve(props.onChanged()).then(() => {
          if (created?.uid) props.onSelect(created.uid);
        });
      })
      .catch(() => message.error("Couldn't add the stage"))
      .finally(() => setBusy(false));
  }

  function onDelete(stage: RoadmapStage): void {
    setBusy(true);
    deleteRoadmapStage(stage.uid)
      .then(() => props.onChanged())
      .catch(() => message.error("Couldn't delete the stage"))
      .finally(() => setBusy(false));
  }

  return (
    <div className="flex h-full flex-col gap-2">
      <Typography.Text strong>Stages</Typography.Text>
      <Space.Compact className="w-full">
        <Input
          placeholder="New stage name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onPressEnter={onAdd}
        />
        <Button type="primary" loading={busy} onClick={onAdd}>
          Add
        </Button>
      </Space.Compact>
      <List
        className="flex-auto overflow-y-auto"
        size="small"
        bordered
        dataSource={props.stages}
        locale={{ emptyText: "No stages yet — add one above" }}
        renderItem={(stage, index) => {
          const selected = stage.uid === props.selectedUid;
          return (
            <List.Item
              className="cursor-pointer"
              style={selected ? { backgroundColor: token.colorPrimaryBg } : undefined}
              onClick={() => props.onSelect(stage.uid)}
            >
              {/* Controlled flex row — antd List `actions` reserves horizontal space and
                  collides with the label inside the narrow rail, so lay it out manually. */}
              <div className="flex w-full min-w-0 flex-row items-center gap-2">
                <Tag className="m-0" color="blue">
                  {stage.order}
                </Tag>
                <DraftTag published={stage.isPublished} />
                <Typography.Text className="min-w-0 flex-auto" ellipsis>
                  {stage.name}
                </Typography.Text>
                <Space.Compact className="shrink-0">
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowUpOutlined />}
                    disabled={index === 0 || reordering}
                    onClick={(e) => {
                      e.stopPropagation();
                      move(index, "up");
                    }}
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowDownOutlined />}
                    disabled={index === props.stages.length - 1 || reordering}
                    onClick={(e) => {
                      e.stopPropagation();
                      move(index, "down");
                    }}
                  />
                  <ConfirmDeleteButton
                    title="Delete this stage?"
                    description="Its requirements and files will be removed with it."
                    onConfirm={() => onDelete(stage)}
                    stopPropagation
                  />
                </Space.Compact>
              </div>
            </List.Item>
          );
        }}
      />
    </div>
  );
}

export default RoadmapStagesRail;
